import { type RoomCheckAction, formatAccreditationNo } from "@seagames/contracts";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { checkAssignments } from "../../lib/api/accommodation";
import { assignmentState, stayOf } from "../../lib/accommodation/types";
import { wallTimeIn } from "../../lib/format/datetime";
import { todayIn } from "../../lib/travel/view";
import { toast } from "../../lib/ui/toast";
import { FlagTags, SexLetter, StayRange } from "../accommodation/parts";
import { PersonStateChip } from "../accommodation/status";
import type { HotelGuest } from "./api";

/** The photo thumb the desk checks a face against (PLAN §0b Q3), or the initials. */
export function GuestPhoto({ guest, className = "" }: { guest: Pick<HotelGuest, "name" | "photo_thumb_url">; className?: string }) {
	const { t } = useTranslation("helpdesk");
	const initials = guest.name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((w) => w[0])
		.join("");
	return guest.photo_thumb_url ? (
		<img
			src={guest.photo_thumb_url}
			alt={t("hotel.photoOf", { name: guest.name })}
			loading="lazy"
			className={`size-[2.75rem] shrink-0 rounded-[0.5rem] bg-gms-soft-2 object-cover ${className}`}
		/>
	) : (
		<span aria-hidden className={`grid size-[2.75rem] shrink-0 place-items-center rounded-[0.5rem] bg-gms-soft-2 text-[0.8125rem] font-semibold text-gms-ink-3 ${className}`}>
			{initials}
		</span>
	);
}

/**
 * One guest, the fields a hotel may see and nothing more: photo, name, AC
 * number, organisation, gender, category, room, stay, state and the row's
 * markers (late arrival, overdue, card withdrawn…). `lead` is the selection
 * checkbox; `end` the row's own action.
 */
export function GuestRow({ guest, lead, end, showBuilding = false }: { guest: HotelGuest; lead?: ReactNode; end?: ReactNode; showBuilding?: boolean }) {
	const { t } = useTranslation("helpdesk");
	const a = guest.assignment;
	return (
		<div className="flex min-w-0 items-start gap-[0.75rem]">
			{lead && <span className="flex h-[2.75rem] shrink-0 items-center">{lead}</span>}
			<GuestPhoto guest={guest} />
			<span className="grid min-w-0 flex-1 gap-[0.125rem]">
				<b className="truncate text-[0.875rem] leading-tight font-semibold text-gms-navy-ink">{guest.name}</b>
				<span className="flex flex-wrap items-center gap-x-[0.375rem] text-[0.75rem] text-gms-ink-3">
					<span className="tabular-nums">{guest.accreditation_no !== null ? formatAccreditationNo(guest.organization_code, guest.accreditation_no) : "—"}</span>
					{guest.organization_code && (
						<>
							<span aria-hidden>·</span>
							<b className="font-semibold text-gms-ink-2">{guest.organization_code}</b>
						</>
					)}
					{guest.gender && (
						<>
							<span aria-hidden>·</span>
							<SexLetter sex={guest.gender} />
						</>
					)}
					{guest.category && (
						<>
							<span aria-hidden>·</span>
							<span>{guest.category}</span>
						</>
					)}
				</span>
				<span className="flex flex-wrap items-center gap-x-[0.625rem] text-[0.75rem] text-gms-ink-2">
					<b className="font-semibold text-gms-navy-ink">{t("hotel.room", { room: a.room_label })}</b>
					{showBuilding && <span className="text-gms-ink-3">{a.building.name}</span>}
					<StayRange stay={stayOf(a)} className="text-[0.75rem]" />
				</span>
				<FlagTags flags={guest.flags} />
			</span>
			<span className="flex shrink-0 flex-col items-end gap-[0.375rem]">
				<PersonStateChip state={assignmentState(a)} />
				{end}
			</span>
		</div>
	);
}

/**
 * Undo a desk mistake on one guest (E2E B3): Undo check-in for a guest who is
 * in, Undo check-out for one checked out TODAY (games' zone) — later than
 * that the bed may already be someone else's and the API refuses anyway.
 * Two steps (confirm inline), one call (`UNDO_IN` / `UNDO_OUT`); the API
 * re-checks the desk permission and the building, and names any refusal.
 * Render it only for a login with the desk permission.
 */
export function UndoCheck({ guest, timeZone }: { guest: HotelGuest; timeZone: string }) {
	const { t } = useTranslation("helpdesk");
	const [asking, setAsking] = useState(false);
	const [busy, setBusy] = useState(false);
	const a = guest.assignment;
	const state = assignmentState(a);
	const action: RoomCheckAction | null =
		state === "IN" ? "UNDO_IN" : state === "OUT" && a.checked_out_at && wallTimeIn(a.checked_out_at, timeZone)?.date === todayIn(timeZone) ? "UNDO_OUT" : null;
	if (!action) return null;
	const label = action === "UNDO_IN" ? t("hotel.undo.in") : t("hotel.undo.out");

	const run = async () => {
		setBusy(true);
		try {
			const res = await checkAssignments(action, [guest.room_assignment_id]);
			const skip = res.skipped[0];
			if (res.done.length > 0) toast.success(action === "UNDO_IN" ? t("hotel.undo.inDone", { name: guest.name }) : t("hotel.undo.outDone", { name: guest.name }));
			else if (skip) toast.info(t("hotel.sheet.skipLine", { name: guest.name, reason: t(`refusal.${skip.reason}`) }));
			setAsking(false);
		} catch (err) {
			toast.error(err, t("hotel.undo.failed"));
		} finally {
			setBusy(false);
		}
	};

	if (!asking)
		return (
			<button type="button" className="btn btn-xs btn-ghost text-gms-navy" onClick={() => setAsking(true)}>
				{label}
			</button>
		);
	return (
		<span role="group" aria-label={label} className="flex flex-wrap items-center justify-end gap-[0.25rem] text-[0.75rem] text-gms-ink-2">
			<span>{action === "UNDO_IN" ? t("hotel.undo.confirmIn") : t("hotel.undo.confirmOut")}</span>
			<button type="button" className="btn btn-xs btn-primary" disabled={busy} onClick={() => void run()}>
				{busy && <span className="loading loading-spinner loading-xs" />}
				{t("hotel.undo.yes")}
			</button>
			<button type="button" className="btn btn-xs btn-line" disabled={busy} onClick={() => setAsking(false)}>
				{t("hotel.undo.no")}
			</button>
		</span>
	);
}
